import { toast } from 'sonner';
import React, { useState } from 'react';
import { Navigate } from 'react-router';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../hooks/index.js';
import { getStepConfig } from '../../getStepConfig';
import { determineIfStepIsDisabled } from '../../utils';
import { usePatientIntakeStatusContext } from '../../hooks';
import { ActionPrompt, ViewDocumentModal } from '../../components';
import { toggleBaseStepVisibilityWithToast } from './utils';
import type { BaseStepsBaseProps } from './BaseSteps.types.ts';

const HealthcareProviderModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => (
  <Dialog open={isOpen} onClose={onClose}>
    <DialogTitle>Consent to Release Health Summary</DialogTitle>
    <DialogContent>
      The patient will generate their letter of consent to release their health summary to us.
    </DialogContent>
  </Dialog>
);

type ConsentToReleaseHealthSummaryBaseStepActionModalProps = {
  step: IntakeStatusBaseSteps['GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY'];
  isOpen: boolean;
  onClose: () => void;
};

const ConsentToReleaseHealthSummaryBaseStepActionModal: React.FC<
  ConsentToReleaseHealthSummaryBaseStepActionModalProps
> = ({ step, isOpen, onClose }) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();

  const { status } = step.currentStatus;

  if (status === 'COMPLETED') {
    return <ViewDocumentModal step={step} type="CONSENT_TO_RELEASE_HEALTH_SUMMARY" isOpen={isOpen} onClose={onClose} />;
  }

  if (isHealthcareProvider) {
    return <HealthcareProviderModal isOpen={isOpen} onClose={onClose} />;
  }

  return <Navigate to="/account-details/documents?hsl=true" />;
};

interface ConsentToReleaseHealthSummaryBaseStepActionProps extends BaseStepsBaseProps {}

export const ConsentToReleaseHealthSummaryBaseStepAction: React.FC<
  ConsentToReleaseHealthSummaryBaseStepActionProps
> = ({ onEdit }) => {
  const { user, userType } = useAuth();
  const { patientId, baseSteps, isHealthcareProvider } = usePatientIntakeStatusContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const step = baseSteps?.GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY;

  const handleOnClick = () => {
    // FUTURE: @keeganpotgieter Change to open a modal instead of navigate in the future
    setIsModalOpen(true);
  };

  const handleOnEdit = () => {
    onEdit();
  };

  const handleOnToggleDisabled = async () => {
    if (!step) {
      toast.error('Step not found');

      return;
    }

    if (!user || !userType || !user.uid || !user.email) {
      toast.error('Please login to continue');

      return;
    }

    const isCurrentlyDisabled = determineIfStepIsDisabled({ step });

    await toggleBaseStepVisibilityWithToast({
      step,
      stepId: 'GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY',
      patientId,
      isCurrentlyDisabled,
      context: 'consent to release health summary',
      actionedBy: {
        userType,
        name: null,
        id: user.uid,
        email: user.email,
      },
    }).unwrap();
  };

  const handleModalOnClose = () => {
    setIsModalOpen(false);
  };

  if (!step) {
    return null;
  }

  const stepConfig = getStepConfig('GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY');

  const title = stepConfig.title({
    step,
    isHealthcareProvider,
  });

  const description = stepConfig.statuses[step.currentStatus.status].description({
    step,
    isHealthcareProvider,
  });

  return (
    <>
      <ActionPrompt
        step={step}
        title={title}
        description={description}
        onClick={handleOnClick}
        onEdit={handleOnEdit}
        onToggleDisabled={handleOnToggleDisabled}
      />

      {isModalOpen && (
        <ConsentToReleaseHealthSummaryBaseStepActionModal
          step={step}
          isOpen={isModalOpen}
          onClose={handleModalOnClose}
        />
      )}
    </>
  );
};
